import { apiDelete, apiGet, apiPatch, apiPost, planningPath } from './PlanningApiClient';
import type { BookingDetail, BookingLine, BookingLineInput, BookingPatch, BookingRow, BookingStatus, QuoteChoice } from '../types/VendorBookingRecord';

/**
 * Vendor bookings (the kal-shape API: GET/PATCH/DELETE /vendor-bookings, POST /quotes/:id/booking). A booking is only ever
 * made from an accepted vendor quote. Nothing here swallows an error: a screen decides how to say it failed.
 */

const enc = encodeURIComponent;
export type BookingView = 'active' | 'archived' | 'all';

const query = (params: Record<string, string | undefined>): string => {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) usp.set(k, v);
  const s = usp.toString();
  return s ? `?${s}` : '';
};

export async function fetchBookings(opts: { view?: BookingView; status?: BookingStatus; vendorId?: string } = {}): Promise<BookingRow[]> {
  const res = await apiGet<{ bookings?: BookingRow[] }>(planningPath(`/vendor-bookings${query({
    archived: opts.view && opts.view !== 'active' ? (opts.view === 'archived' ? 'true' : 'all') : undefined,
    status: opts.status, vendor_id: opts.vendorId,
  })}`));
  return Array.isArray(res?.bookings) ? res.bookings : [];
}

export const fetchBooking = (id: string) => apiGet<BookingDetail>(planningPath(`/vendor-bookings/${enc(id)}`));

export const updateBooking = (id: string, patch: BookingPatch) =>
  apiPatch<BookingDetail>(planningPath(`/vendor-bookings/${enc(id)}`), patch);

export const archiveBooking = (id: string) => apiDelete<{ id: string; archived: boolean }>(planningPath(`/vendor-bookings/${enc(id)}`));
export const restoreBooking = (id: string) => apiDelete<{ id: string; archived: boolean }>(planningPath(`/vendor-bookings/${enc(id)}?restore=true`));

/** Creates the booking from an accepted quote. Refused (409 booking_exists, with booking_id) when the quote already has a live one. */
export const createBookingFromQuote = (quoteId: string) =>
  apiPost<BookingDetail>(planningPath(`/quotes/${enc(quoteId)}/booking`), {});

export const addBookingLine = (bookingId: string, input: BookingLineInput) =>
  apiPost<BookingLine>(planningPath(`/vendor-bookings/${enc(bookingId)}/lines`), input);
export const updateBookingLine = (bookingId: string, lineId: string, input: BookingLineInput) =>
  apiPatch<BookingLine>(planningPath(`/vendor-bookings/${enc(bookingId)}/lines/${enc(lineId)}`), input);
export const deleteBookingLine = (bookingId: string, lineId: string) =>
  apiDelete(planningPath(`/vendor-bookings/${enc(bookingId)}/lines/${enc(lineId)}`));

/** Accepted, not archived quotes: the ones a booking can start from. */
export async function fetchAcceptedQuotes(): Promise<QuoteChoice[]> {
  const res = await apiGet<{ quotes?: QuoteChoice[] }>(planningPath('/quotes'));
  return (Array.isArray(res?.quotes) ? res.quotes : []).filter(q => q.status === 'accepted' && !q.archived);
}
